import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Plus, Edit2, Trash2, LogOut, Eye, EyeOff, Radio, X, Check,
  Mail, Users, FileText, Upload, BookOpen, RefreshCw, Share2, PenLine, Bell, BarChart3
} from "lucide-react";
import { AnalyticsTab } from "./AnalyticsTab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Naval History", "Political Science", "Voting", "AD0K Radio", "Music", "Writing", "Misc"];
const PAPER_CATEGORIES = ["Naval History", "Political Science", "Voting", "Military Affairs", "Radio", "Writing", "Books", "Music"];

// ── Tiptap Toolbar Button ──────────────────────────────────────────────────
function ToolBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
        active ? "bg-[#0A1628] text-[#F5F0E8]" : "text-[#2D3142] hover:bg-[#0A1628]/10"
      }`}
    >
      {children}
    </button>
  );
}

// ── Tiptap Editor Component ────────────────────────────────────────────────
function TiptapEditor({ initialContent, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false }),
    ],
    content: initialContent || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  return (
    <div className="tiptap-wrapper border border-[#0A1628]/20 bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-[#0A1628]/10 bg-[#f9f7f2]">
        <ToolBtn title="Heading 2" active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolBtn>
        <ToolBtn title="Heading 3" active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolBtn>
        <span className="w-px h-4 bg-[#0A1628]/15 mx-1" />
        <ToolBtn title="Bold" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></ToolBtn>
        <ToolBtn title="Italic" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolBtn>
        <span className="w-px h-4 bg-[#0A1628]/15 mx-1" />
        <ToolBtn title="Bullet list" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</ToolBtn>
        <ToolBtn title="Ordered list" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</ToolBtn>
        <span className="w-px h-4 bg-[#0A1628]/15 mx-1" />
        <ToolBtn title="Blockquote" active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>Quote</ToolBtn>
        <ToolBtn title="Clear formatting" active={false}
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>Clear</ToolBtn>
      </div>
      {/* Editor area */}
      <EditorContent
        data-testid="post-content-input"
        editor={editor}
        className="tiptap-editor"
      />
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isContentEmpty(html) {
  return !html || html.replace(/<[^>]*>/g, "").trim().length === 0;
}

const EMPTY_FORM = { title: "", excerpt: "", content: "", categories: ["Naval History"], published: false };

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [expandedContact, setExpandedContact] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(true);
  const [bookNotify, setBookNotify] = useState([]);
  const [bookNotifyLoading, setBookNotifyLoading] = useState(true);
  const [addSubForm, setAddSubForm] = useState({ name: "", email: "" });
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [addSubSaving, setAddSubSaving] = useState(false);
  const [addSubError, setAddSubError] = useState("");
  const [confirmDeleteSubId, setConfirmDeleteSubId] = useState(null);
  const [confirmDeleteContactId, setConfirmDeleteContactId] = useState(null);
  const [editSubId, setEditSubId] = useState(null);
  const [editSubForm, setEditSubForm] = useState({ name: "", email: "" });
  const [editSubSaving, setEditSubSaving] = useState(false);
  const [editSubError, setEditSubError] = useState("");

  // Papers state
  const [papers, setPapers] = useState([]);
  const [papersLoading, setPapersLoading] = useState(true);
  const [paperDialogOpen, setPaperDialogOpen] = useState(false);
  const [editingPaper, setEditingPaper] = useState(null);
  const [paperForm, setPaperForm] = useState({ title: "", abstract: "", year: new Date().getFullYear(), categories: ["Naval History"] });
  const [paperSaving, setPaperSaving] = useState(false);
  const [paperFormError, setPaperFormError] = useState("");
  const [deletePaperId, setDeletePaperId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadModalPaperId, setUploadModalPaperId] = useState(null);
  const [uploadTab, setUploadTab] = useState("computer");
  const [urlInput, setUrlInput] = useState("");
  const [urlUploading, setUrlUploading] = useState(false);
  const [urlError, setUrlError] = useState("");

  // Essays state
  const [essays, setEssays] = useState([]);
  const [essaysLoading, setEssaysLoading] = useState(true);
  const [essayDialogOpen, setEssayDialogOpen] = useState(false);
  const [editingEssay, setEditingEssay] = useState(null);
  const [essayForm, setEssayForm] = useState({ title: "", abstract: "", content: "", year: new Date().getFullYear(), categories: ["Naval History"] });
  const [essaySaving, setEssaySaving] = useState(false);
  const [essayFormError, setEssayFormError] = useState("");
  const [deleteEssayId, setDeleteEssayId] = useState(null);
  const [uploadingEssayId, setUploadingEssayId] = useState(null);
  const [uploadModalEssayId, setUploadModalEssayId] = useState(null);
  const [seedConfirm, setSeedConfirm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // Social share state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTitle, setShareTitle] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [sharePlatforms, setSharePlatforms] = useState({ bluesky: true });
  const [shareSaving, setShareSaving] = useState(false);
  const [shareResults, setShareResults] = useState(null);

  useEffect(() => { document.title = "Admin | Donald Inbody"; }, []);

  const fetchPosts = useCallback(() => {
    setPostsLoading(true);
    axios.get(`${API}/blog/admin/posts`, { withCredentials: true })
      .then(r => setPosts(r.data))
      .catch(err => { if (err.response?.status === 401) navigate("/admin"); })
      .finally(() => setPostsLoading(false));
  }, [navigate]);

  const fetchContacts = useCallback(() => {
    axios.get(`${API}/admin/contacts`, { withCredentials: true })
      .then(r => setContacts(r.data))
      .catch(() => {})
      .finally(() => setContactsLoading(false));
  }, []);

  const fetchSubscribers = useCallback(() => {
    axios.get(`${API}/admin/subscribers`, { withCredentials: true })
      .then(r => setSubscribers(r.data))
      .catch(() => {})
      .finally(() => setSubscribersLoading(false));
  }, []);

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!addSubForm.email.trim()) { setAddSubError("Email is required."); return; }
    setAddSubSaving(true);
    setAddSubError("");
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/subscribers`,
        { name: addSubForm.name.trim(), email: addSubForm.email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscribers(prev => [res.data, ...prev]);
      setAddSubForm({ name: "", email: "" });
      setAddSubOpen(false);
    } catch (err) {
      setAddSubError(err.response?.data?.detail || "Failed to add subscriber.");
    } finally {
      setAddSubSaving(false);
    }
  };

  const handleDeleteSubscriber = async (id) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete subscriber.");
    } finally {
      setConfirmDeleteSubId(null);
    }
  };

  const handleDeleteContact = async (id) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(prev => prev.filter(c => c.id !== id));
      setExpandedContact(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete contact.");
    } finally {
      setConfirmDeleteContactId(null);
    }
  };

  const handleEditSubSave = async (id) => {
    if (!editSubForm.email.trim()) { setEditSubError("Email is required."); return; }
    setEditSubSaving(true);
    setEditSubError("");
    const token = localStorage.getItem("admin_token");
    try {
      const res = await axios.put(`${API}/admin/subscribers/${id}`,
        { name: editSubForm.name.trim(), email: editSubForm.email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscribers(prev => prev.map(s => s.id === id ? res.data : s));
      setEditSubId(null);
    } catch (err) {
      setEditSubError(err.response?.data?.detail || "Failed to save changes.");
    } finally {
      setEditSubSaving(false);
    }
  };

  const fetchPapers = useCallback(() => {
    setPapersLoading(true);
    axios.get(`${API}/papers`, { withCredentials: true })
      .then(r => setPapers(r.data))
      .catch(() => {})
      .finally(() => setPapersLoading(false));
  }, []);

  const fetchEssays = useCallback(() => {
    setEssaysLoading(true);
    axios.get(`${API}/essays`, { withCredentials: true })
      .then(r => setEssays(r.data))
      .catch(() => {})
      .finally(() => setEssaysLoading(false));
  }, []);

  const fetchBookNotify = useCallback(() => {
    setBookNotifyLoading(true);
    axios.get(`${API}/admin/book-notify`, { withCredentials: true })
      .then(r => setBookNotify(r.data))
      .catch(() => {})
      .finally(() => setBookNotifyLoading(false));
  }, []);

  const handleDeleteNotify = async (id) => {
    try {
      await axios.delete(`${API}/admin/book-notify/${id}`, { withCredentials: true });
      setBookNotify(prev => prev.filter(e => e.id !== id));
    } catch { /* ignore */ }
  };

  // Fetch all on mount so stats row is accurate
  useEffect(() => {
    fetchPosts();
    fetchContacts();
    fetchSubscribers();
    fetchPapers();
    fetchEssays();
    fetchBookNotify();
  }, [fetchPosts, fetchContacts, fetchSubscribers, fetchPapers, fetchEssays, fetchBookNotify]);

  const openCreate = () => {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      categories: post.categories || (post.category ? [post.category] : ["Naval History"]),
      published: post.published,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError("Title is required."); return; }
    if (!form.excerpt.trim()) { setFormError("Excerpt is required."); return; }
    if (isContentEmpty(form.content)) { setFormError("Content is required."); return; }
    setSaving(true);
    setFormError("");
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/blog/posts`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchPosts();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/blog/posts/${deleteId}`, { withCredentials: true });
      fetchPosts();
    } catch {}
    setDeleteId(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setSeedResult({ ok: true, counts: res.data.counts });
      fetchPosts();
      fetchPapers();
    } catch (err) {
      setSeedResult({ ok: false, error: err.response?.data?.detail || "Seed failed." });
    } finally {
      setSeeding(false);
      setSeedConfirm(false);
    }
  };

  // ── Paper handlers ──
  const openCreatePaper = () => {
    setEditingPaper(null);
    setPaperForm({ title: "", abstract: "", year: new Date().getFullYear(), categories: ["Naval History"] });
    setPaperFormError("");
    setPaperDialogOpen(true);
  };

  const openEditPaper = (paper) => {
    setEditingPaper(paper);
    setPaperForm({ title: paper.title, abstract: paper.abstract, year: paper.year, categories: paper.categories || (paper.category ? [paper.category] : ["Naval History"]) });
    setPaperFormError("");
    setPaperDialogOpen(true);
  };

  const handleSavePaper = async () => {
    if (!paperForm.title.trim()) { setPaperFormError("Title is required."); return; }
    if (!paperForm.abstract.trim()) { setPaperFormError("Abstract is required."); return; }
    if (!paperForm.year) { setPaperFormError("Year is required."); return; }
    setPaperSaving(true);
    setPaperFormError("");
    try {
      if (editingPaper) {
        await axios.put(`${API}/papers/${editingPaper.id}`, paperForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/papers`, paperForm, { withCredentials: true });
      }
      setPaperDialogOpen(false);
      fetchPapers();
    } catch (err) {
      setPaperFormError(err.response?.data?.detail || "Save failed.");
    } finally {
      setPaperSaving(false);
    }
  };

  const handleDeletePaper = async () => {
    if (!deletePaperId) return;
    try {
      await axios.delete(`${API}/papers/${deletePaperId}`, { withCredentials: true });
      fetchPapers();
    } catch {}
    setDeletePaperId(null);
  };

  const handlePdfUpload = async (paperId, file) => {
    if (!file) return;
    setUploadingId(paperId);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/papers/${paperId}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchPapers();
      setUploadModalPaperId(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingId(null);
    }
  };

  const handleUrlUpload = async (paperId) => {
    if (!urlInput.trim()) return;
    setUrlUploading(true);
    setUrlError("");
    try {
      await axios.post(`${API}/papers/${paperId}/upload-url`, { url: urlInput.trim() }, { withCredentials: true });
      fetchPapers();
      setUploadModalPaperId(null);
      setUrlInput("");
    } catch (err) {
      setUrlError(err.response?.data?.detail || "Failed to fetch PDF from URL.");
    } finally {
      setUrlUploading(false);
    }
  };

  // ── Essay handlers ──
  const openCreateEssay = () => {
    setEditingEssay(null);
    setEssayForm({ title: "", abstract: "", content: "", year: new Date().getFullYear(), categories: ["Naval History"] });
    setEssayFormError("");
    setEssayDialogOpen(true);
  };

  const openEditEssay = (essay) => {
    setEditingEssay(essay);
    setEssayForm({ title: essay.title, abstract: essay.abstract, content: essay.content || "", year: essay.year, categories: essay.categories || ["Naval History"] });
    setEssayFormError("");
    setEssayDialogOpen(true);
  };

  const handleSaveEssay = async () => {
    if (!essayForm.title.trim()) { setEssayFormError("Title is required."); return; }
    if (!essayForm.abstract.trim()) { setEssayFormError("Abstract is required."); return; }
    if (!essayForm.year) { setEssayFormError("Year is required."); return; }
    setEssaySaving(true);
    setEssayFormError("");
    try {
      if (editingEssay) {
        await axios.put(`${API}/essays/${editingEssay.id}`, essayForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/essays`, essayForm, { withCredentials: true });
      }
      setEssayDialogOpen(false);
      fetchEssays();
    } catch (err) {
      setEssayFormError(err.response?.data?.detail || "Save failed.");
    } finally {
      setEssaySaving(false);
    }
  };

  const handleDeleteEssay = async () => {
    if (!deleteEssayId) return;
    try {
      await axios.delete(`${API}/essays/${deleteEssayId}`, { withCredentials: true });
      fetchEssays();
    } catch {}
    setDeleteEssayId(null);
  };

  const handleEssayFileUpload = async (essayId, file) => {
    if (!file) return;
    setUploadingEssayId(essayId);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/essays/${essayId}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchEssays();
      setUploadModalEssayId(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingEssayId(null);
    }
  };

  const handleEssayUrlUpload = async (essayId) => {
    if (!urlInput.trim()) return;
    setUrlUploading(true);
    setUrlError("");
    try {
      await axios.post(`${API}/essays/${essayId}/upload-url`, { url: urlInput.trim() }, { withCredentials: true });
      fetchEssays();
      setUploadModalEssayId(null);
      setUrlInput("");
    } catch (err) {
      setUrlError(err.response?.data?.detail || "Failed to fetch PDF from URL.");
    } finally {
      setUrlUploading(false);
    }
  };

  const openGooglePicker = async (paperId) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!apiKey || !clientId) {
      setUrlError("Google Drive is not configured. See setup instructions below.");
      return;
    }

    // Load gapi + GSI scripts if not already loaded
    const loadScript = (src) => new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
      const s = document.createElement("script");
      s.src = src; s.onload = resolve; s.onerror = reject;
      document.body.appendChild(s);
    });

    try {
      await loadScript("https://apis.google.com/js/api.js");
      await new Promise(resolve => window.gapi.load("picker", resolve));
      await loadScript("https://accounts.google.com/gsi/client");

      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "https://www.googleapis.com/auth/drive.readonly",
        callback: async (tokenResponse) => {
          if (tokenResponse.error) { setUrlError("Google sign-in failed."); return; }

          const picker = new window.google.picker.PickerBuilder()
            .addView(new window.google.picker.DocsView().setMimeTypes("application/pdf"))
            .setOAuthToken(tokenResponse.access_token)
            .setDeveloperKey(apiKey)
            .setCallback(async (data) => {
              if (data.action !== window.google.picker.Action.PICKED) return;
              const driveFile = data.docs[0];
              setUploadingId(paperId);
              try {
                const res = await fetch(
                  `https://www.googleapis.com/drive/v3/files/${driveFile.id}?alt=media`,
                  { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
                );
                if (!res.ok) throw new Error("Download failed");
                const blob = await res.blob();
                const pdfFile = new File([blob], driveFile.name || "paper.pdf", { type: "application/pdf" });
                const formData = new FormData();
                formData.append("file", pdfFile);
                await axios.post(`${API}/papers/${paperId}/upload`, formData, {
                  withCredentials: true,
                  headers: { "Content-Type": "multipart/form-data" },
                });
                fetchPapers();
                setUploadModalPaperId(null);
              } catch {
                setUrlError("Failed to download from Google Drive.");
              } finally {
                setUploadingId(null);
              }
            })
            .build();
          picker.setVisible(true);
        },
      });
      tokenClient.requestAccessToken({ prompt: "" });
    } catch {
      setUrlError("Failed to load Google Drive picker.");
    }
  };

  const openShare = (title, excerpt, url) => {
    const msg = excerpt?.length > 200 ? excerpt.slice(0, 200) + "…" : (excerpt || title);
    setShareTitle(title);
    setShareUrl(url);
    setShareMsg(msg);
    setShareResults(null);
    setSharePlatforms({ bluesky: true });
    setShareOpen(true);
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    const platforms = Object.entries(sharePlatforms).filter(([, v]) => v).map(([k]) => k);
    if (platforms.length === 0) return;
    setShareSaving(true);
    setShareResults(null);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/admin/social/share`,
        { message: shareMsg, url: shareUrl, platforms },
        { withCredentials: true, headers }
      );
      setShareResults(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errMsg =
        typeof detail === "string"
          ? detail
          : detail
          ? JSON.stringify(detail)
          : err.message || "Request failed";
      setShareResults({ _error: `[${err.response?.status ?? "Network error"}] ${errMsg}` });
    } finally {
      setShareSaving(false);
    }
  };

  const tabs = [
    { id: "posts", label: "Blog Posts", icon: FileText, count: posts.length },
    { id: "papers", label: "Papers", icon: BookOpen, count: papers.length },
    { id: "essays", label: "Essays", icon: PenLine, count: essays.length },
    { id: "newsletter", label: "Newsletter", icon: Mail, count: 0 },
    { id: "contacts", label: "Contact Submissions", icon: Mail, count: contacts.length },
    { id: "subscribers", label: "Mailing List", icon: Users, count: subscribers.length },
    { id: "book-notify", label: "Launch Notify", icon: Bell, count: bookNotify.length },
    { id: "analytics", label: "Page Views", icon: BarChart3, count: 0 },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      {/* Top Bar */}
      <header className="bg-[#0A1628] border-b border-[#F5F0E8]/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#C5A059] flex items-center justify-center">
              <Radio size={16} className="text-[#0A1628]" />
            </div>
            <div>
              <span className="font-serif text-[#F5F0E8] font-bold text-sm">Site Administration</span>
              <span className="text-[#F5F0E8]/40 text-xs ml-3">{user?.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-[#F5F0E8]/50 hover:text-[#F5F0E8] text-xs transition-colors">
              View Site
            </a>
            {!seedConfirm ? (
              <button
                data-testid="reseed-btn"
                onClick={() => { setSeedConfirm(true); setSeedResult(null); }}
                className="inline-flex items-center gap-1.5 text-[#F5F0E8]/50 hover:text-[#C5A059] text-xs transition-colors"
                title="Re-seed database from hardcoded data"
              >
                <RefreshCw size={13} /> Reseed DB
              </button>
            ) : (
              <span className="flex items-center gap-2 text-xs">
                <span className="text-[#C5A059]">Reseed all data?</span>
                <button
                  data-testid="confirm-seed-btn"
                  onClick={handleSeed}
                  disabled={seeding}
                  className="bg-[#C5A059] text-[#0A1628] px-2 py-0.5 font-semibold hover:bg-[#F5F0E8] transition-colors disabled:opacity-50"
                >
                  {seeding ? "Running…" : "Yes"}
                </button>
                <button
                  onClick={() => setSeedConfirm(false)}
                  className="text-[#F5F0E8]/40 hover:text-[#F5F0E8] transition-colors"
                >
                  Cancel
                </button>
              </span>
            )}
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-[#F5F0E8]/60 hover:text-[#F5F0E8] text-sm transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Posts", value: posts.length },
            { label: "Papers", value: papers.length },
            { label: "Contact Messages", value: contacts.length },
            { label: "Subscribers", value: subscribers.length },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-[#0A1628]/10 p-5">
              <div className="font-serif text-3xl font-bold text-[#0A1628]">{value}</div>
              <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Seed result banner */}
        {seedResult && (
          <div
            data-testid="seed-result"
            className={`mb-6 px-5 py-3 border text-sm flex items-center justify-between ${
              seedResult.ok
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <span>
              {seedResult.ok
                ? `Seed complete — ${seedResult.counts.blog_posts ?? "?"} blog posts, ${seedResult.counts.papers ?? "?"} papers in database.`
                : `Seed error: ${seedResult.error}`}
            </span>
            <button onClick={() => setSeedResult(null)} className="ml-4 opacity-60 hover:opacity-100 transition-opacity">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-0 border-b border-[#0A1628]/15 mb-8">
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === id
                  ? "border-[#0A1628] text-[#0A1628]"
                  : "border-transparent text-[#2D3142]/50 hover:text-[#0A1628]"
              }`}
            >
              <Icon size={14} />
              {label}
              {count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === id ? "bg-[#0A1628] text-[#F5F0E8]" : "bg-[#0A1628]/10 text-[#2D3142]"}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Blog Posts Tab ── */}
        {activeTab === "posts" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 data-testid="dashboard-title" className="font-serif text-2xl font-bold text-[#0A1628]">Blog Posts</h1>
              <Button
                onClick={openCreate}
                data-testid="create-post-btn"
                className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none gap-2"
              >
                <Plus size={16} /> New Post
              </Button>
            </div>

            {postsLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : posts.length === 0 ? (
              <div data-testid="empty-posts" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <p className="font-serif text-xl text-[#0A1628] mb-2">No posts yet</p>
                <p className="text-[#2D3142] text-sm mb-4">Create your first blog post to get started.</p>
                <Button onClick={openCreate} className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none">
                  <Plus size={14} className="mr-2" /> Create First Post
                </Button>
              </div>
            ) : (
              <div data-testid="posts-table" className="bg-white border border-[#0A1628]/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#0A1628]/10">
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Title</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Category</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Status</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Date</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Reads</TableHead>
                      <TableHead className="text-right text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {posts.map(post => (
                      <TableRow key={post.id} data-testid={`post-row-${post.id}`} className="border-[#0A1628]/8 hover:bg-[#F5F0E8]/50">
                        <TableCell>
                          <div className="font-medium text-[#0A1628] text-sm">{post.title}</div>
                          <div className="text-[#2D3142]/50 text-xs mt-0.5 line-clamp-1">{post.excerpt}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(post.categories || [post.category]).map(cat => (
                              <span key={cat} className="text-xs bg-[#F5F0E8] text-[#2D3142] px-2 py-1 border border-[#0A1628]/10">
                                {cat}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            data-testid={`post-status-${post.id}`}
                            className={`text-xs rounded-none ${post.published ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"}`}
                          >
                            {post.published
                              ? <><Check size={10} className="mr-1" />Published</>
                              : <><EyeOff size={10} className="mr-1" />Draft</>}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#2D3142] text-xs">{formatDate(post.created_at)}</TableCell>
                        <TableCell className="text-[#2D3142] text-xs font-mono">{post.views ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`view-post-${post.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                            >
                              <Eye size={14} />
                            </a>
                            <button
                              onClick={() => {
                                const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
                                openShare(post.title, post.excerpt, `${frontendUrl}/blog/${post.slug}`);
                              }}
                              data-testid={`share-post-${post.id}`}
                              title="Share to social media"
                              className="p-1.5 text-[#2D3142]/50 hover:text-[#C5A059] transition-colors"
                            >
                              <Share2 size={14} />
                            </button>
                            <button
                              onClick={() => openEdit(post)}
                              data-testid={`edit-post-${post.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteId(post.id)}
                              data-testid={`delete-post-${post.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ── Papers Tab ── */}
        {activeTab === "papers" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 data-testid="papers-admin-title" className="font-serif text-2xl font-bold text-[#0A1628]">Papers &amp; Manuscripts</h1>
              <Button
                onClick={openCreatePaper}
                data-testid="create-paper-btn"
                className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none gap-2"
              >
                <Plus size={16} /> New Paper
              </Button>
            </div>

            {papersLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : papers.length === 0 ? (
              <div data-testid="empty-papers" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <BookOpen size={28} className="text-[#C5A059] mx-auto mb-3" />
                <p className="font-serif text-xl text-[#0A1628] mb-2">No papers yet</p>
                <p className="text-[#2D3142] text-sm mb-4">Add your first paper or manuscript.</p>
                <Button onClick={openCreatePaper} className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none">
                  <Plus size={14} className="mr-2" /> Add First Paper
                </Button>
              </div>
            ) : (
              <div data-testid="papers-table" className="bg-white border border-[#0A1628]/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#0A1628]/10">
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Title</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Category</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Year</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">PDF</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Reads</TableHead>
                      <TableHead className="text-right text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {papers.map(paper => (
                      <TableRow key={paper.id} data-testid={`paper-row-${paper.id}`} className="border-[#0A1628]/8 hover:bg-[#F5F0E8]/50">
                        <TableCell>
                          <div className="font-medium text-[#0A1628] text-sm">{paper.title}</div>
                          <div className="text-[#2D3142]/50 text-xs mt-0.5 line-clamp-1">{paper.abstract}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(paper.categories || [paper.category]).map(cat => (
                              <span key={cat} className="text-xs bg-[#F5F0E8] text-[#2D3142] px-2 py-1 border border-[#0A1628]/10">
                                {cat}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-[#2D3142] text-sm font-mono">{paper.year}</TableCell>
                        <TableCell>
                          {paper.has_pdf ? (
                            <div className="flex items-center gap-2">
                              <Badge className="text-xs rounded-none bg-green-100 text-green-800 hover:bg-green-100">
                                <Check size={10} className="mr-1" /> Uploaded
                              </Badge>
                              <button
                                data-testid={`replace-pdf-${paper.id}`}
                                onClick={() => { setUploadModalPaperId(paper.id); setUploadTab("computer"); setUrlInput(""); setUrlError(""); }}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#0A1628]/20 text-[#2D3142]/60 hover:border-[#0A1628] hover:text-[#0A1628] transition-colors"
                              >
                                <Upload size={9} /> Replace
                              </button>
                            </div>
                          ) : (
                            <button
                              data-testid={`upload-pdf-${paper.id}`}
                              onClick={() => { setUploadModalPaperId(paper.id); setUploadTab("computer"); setUrlInput(""); setUrlError(""); }}
                              disabled={uploadingId === paper.id}
                              className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors ${
                                uploadingId === paper.id
                                  ? "border-[#0A1628]/20 text-[#2D3142]/40 cursor-not-allowed"
                                  : "border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                              }`}
                            >
                              <Upload size={10} />
                              {uploadingId === paper.id ? "Uploading…" : "Upload PDF"}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-[#2D3142] text-xs font-mono">{paper.views ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {paper.has_pdf && (
                              <a
                                href={`${API}/papers/${paper.id}/pdf`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`view-paper-${paper.id}`}
                                className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                                title="View PDF"
                              >
                                <Eye size={14} />
                              </a>
                            )}
                            <button
                              onClick={() => openEditPaper(paper)}
                              data-testid={`edit-paper-${paper.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setDeletePaperId(paper.id)}
                              data-testid={`delete-paper-${paper.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ── Essays Tab ── */}
        {activeTab === "essays" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 data-testid="essays-admin-title" className="font-serif text-2xl font-bold text-[#0A1628]">Essays</h1>
              <Button
                onClick={openCreateEssay}
                data-testid="create-essay-btn"
                className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none gap-2"
              >
                <Plus size={16} /> New Essay
              </Button>
            </div>

            {essaysLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : essays.length === 0 ? (
              <div data-testid="empty-essays" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <PenLine size={28} className="text-[#C5A059] mx-auto mb-3" />
                <p className="font-serif text-xl text-[#0A1628] mb-2">No essays yet</p>
                <p className="text-[#2D3142] text-sm mb-4">Add your first essay.</p>
                <Button onClick={openCreateEssay} className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none">
                  <Plus size={14} className="mr-2" /> Add First Essay
                </Button>
              </div>
            ) : (
              <div data-testid="essays-table" className="bg-white border border-[#0A1628]/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#0A1628]/10">
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Title</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Category</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Year</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">File</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Reads</TableHead>
                      <TableHead className="text-right text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {essays.map(essay => (
                      <TableRow key={essay.id} data-testid={`essay-row-${essay.id}`} className="border-[#0A1628]/8 hover:bg-[#F5F0E8]/50">
                        <TableCell>
                          <div className="font-medium text-[#0A1628] text-sm">{essay.title}</div>
                          <div className="text-[#2D3142]/50 text-xs mt-0.5 line-clamp-1">{essay.abstract}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(essay.categories || []).map(cat => (
                              <span key={cat} className="text-xs bg-[#F5F0E8] text-[#2D3142] px-2 py-1 border border-[#0A1628]/10">{cat}</span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-[#2D3142] text-sm font-mono">{essay.year}</TableCell>
                        <TableCell>
                          {essay.has_pdf ? (
                            <div className="flex items-center gap-2">
                              <Badge className="text-xs rounded-none bg-green-100 text-green-800 hover:bg-green-100">
                                <Check size={10} className="mr-1" /> Uploaded
                              </Badge>
                              <button
                                data-testid={`replace-essay-file-${essay.id}`}
                                onClick={() => { setUploadModalEssayId(essay.id); setUploadTab("computer"); setUrlInput(""); setUrlError(""); }}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#0A1628]/20 text-[#2D3142]/60 hover:border-[#0A1628] hover:text-[#0A1628] transition-colors"
                              >
                                <Upload size={9} /> Replace
                              </button>
                            </div>
                          ) : (
                            <button
                              data-testid={`upload-essay-file-${essay.id}`}
                              onClick={() => { setUploadModalEssayId(essay.id); setUploadTab("computer"); setUrlInput(""); setUrlError(""); }}
                              disabled={uploadingEssayId === essay.id}
                              className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors ${
                                uploadingEssayId === essay.id
                                  ? "border-[#0A1628]/20 text-[#2D3142]/40 cursor-not-allowed"
                                  : "border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                              }`}
                            >
                              <Upload size={10} />
                              {uploadingEssayId === essay.id ? "Uploading…" : "Upload PDF"}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-[#2D3142] text-xs font-mono">{essay.views ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {essay.has_pdf && (
                              <a
                                href={`${API}/essays/${essay.id}/pdf`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`view-essay-${essay.id}`}
                                className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                                title="View file"
                              >
                                <Eye size={14} />
                              </a>
                            )}
                            <button
                              onClick={() => openEditEssay(essay)}
                              data-testid={`edit-essay-${essay.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteEssayId(essay.id)}
                              data-testid={`delete-essay-${essay.id}`}
                              className="p-1.5 text-[#2D3142]/50 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ── Contacts Tab ── */}
        {activeTab === "contacts" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 data-testid="contacts-title" className="font-serif text-2xl font-bold text-[#0A1628]">Contact Submissions</h1>
              <span className="text-[#2D3142]/50 text-sm">{contacts.length} message{contacts.length !== 1 ? "s" : ""}</span>
            </div>

            {contactsLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : contacts.length === 0 ? (
              <div data-testid="empty-contacts" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <Mail size={28} className="text-[#C5A059] mx-auto mb-3" />
                <p className="font-serif text-xl text-[#0A1628] mb-2">No messages yet</p>
                <p className="text-[#2D3142] text-sm">Contact form submissions will appear here.</p>
              </div>
            ) : (
              <div data-testid="contacts-table" className="space-y-3">
                {contacts.map(contact => (
                  <div key={contact.id} data-testid={`contact-row-${contact.id}`} className="bg-white border border-[#0A1628]/10">
                    <button
                      className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#F5F0E8]/50 transition-colors"
                      onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-9 h-9 bg-[#0A1628] flex items-center justify-center flex-shrink-0 text-[#C5A059] font-serif font-bold text-sm">
                          {contact.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-[#0A1628] text-sm">{contact.name}</div>
                          <div className="text-[#2D3142]/50 text-xs truncate">{contact.email}</div>
                        </div>
                        <div className="hidden sm:block text-[#2D3142] text-sm font-medium truncate max-w-xs">{contact.subject}</div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-[#2D3142]/40 text-xs">{formatDate(contact.created_at)}</span>
                        <span className={`text-[#2D3142]/40 text-xs transition-transform ${expandedContact === contact.id ? "rotate-180" : ""}`}>▾</span>
                      </div>
                    </button>
                    {expandedContact === contact.id && (
                      <div className="px-5 pb-5 border-t border-[#0A1628]/8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 pt-4">
                          <div>
                            <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">From</div>
                            <div className="text-sm text-[#0A1628]">{contact.name}</div>
                          </div>
                          <div>
                            <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">Email</div>
                            <a href={`mailto:${contact.email}`} className="text-sm text-[#C5A059] hover:underline">{contact.email}</a>
                          </div>
                          <div className="sm:col-span-2">
                            <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">Subject</div>
                            <div className="text-sm font-medium text-[#0A1628]">{contact.subject}</div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-2">Message</div>
                          <div className="bg-[#F5F0E8] border border-[#0A1628]/10 p-4 text-sm text-[#2D3142] leading-relaxed whitespace-pre-wrap">
                            {contact.message}
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                          <a
                            href={`mailto:${contact.email}?subject=Re: ${encodeURIComponent(contact.subject)}`}
                            className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-4 py-2 text-xs font-semibold hover:bg-[#2D3142] transition-colors"
                          >
                            <Mail size={12} /> Reply via Email
                          </a>
                          {confirmDeleteContactId === contact.id ? (
                            <span className="flex items-center gap-2 text-xs">
                              <span className="text-red-600 font-medium">Delete this message?</span>
                              <button
                                data-testid={`confirm-delete-contact-${contact.id}`}
                                onClick={() => handleDeleteContact(contact.id)}
                                className="bg-red-600 text-white px-3 py-1 text-xs font-semibold hover:bg-red-700 transition-colors"
                              >Yes, delete</button>
                              <button
                                onClick={() => setConfirmDeleteContactId(null)}
                                className="text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                              >Cancel</button>
                            </span>
                          ) : (
                            <button
                              data-testid={`delete-contact-${contact.id}`}
                              onClick={() => setConfirmDeleteContactId(contact.id)}
                              className="inline-flex items-center gap-1 text-xs text-[#2D3142]/40 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Newsletter Tab ── */}
        {activeTab === "newsletter" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-serif text-2xl font-bold text-[#0A1628]">The Dispatch</h1>
                <p className="text-[#2D3142]/50 text-sm mt-0.5">Share newsletter issues to social media</p>
              </div>
              <a
                href="/newsletter"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-xs text-[#C5A059] border border-[#C5A059]/40 px-3 py-2 hover:bg-[#C5A059]/10 transition-colors"
              >
                <Eye size={13} /> View Newsletter
              </a>
            </div>

            <div className="space-y-4">
              {[
                {
                  id: "26-002",
                  label: "No. 26-002 — 07 May 2026",
                  tag: "Current Issue",
                  blurb: "Issue 26-002: Website updates, Wake of the Falcon in its final stages, AD0K radio page reworked with live status and propagation indicators.",
                },
                {
                  id: "26-001",
                  label: "No. 26-001 — 05 May 2026",
                  tag: "Archive",
                  blurb: "Issue 26-001: Wake of the Falcon in final cleanup, new hardline to the tower, Rhiannon Giddens at Bass Auditorium.",
                },
              ].map(issue => (
                <div
                  key={issue.id}
                  className="flex items-start justify-between gap-6 p-5 bg-white border border-[#0A1628]/10"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[#C5A059] text-xs font-bold uppercase tracking-widest">{issue.tag}</span>
                    </div>
                    <p className="font-serif font-semibold text-[#0A1628] text-base">{issue.label}</p>
                    <p className="text-[#2D3142]/60 text-sm mt-1 leading-relaxed">{issue.blurb}</p>
                  </div>
                  <button
                    data-testid={`share-newsletter-${issue.id}`}
                    onClick={() => openShare(
                      `The Dispatch — ${issue.label}`,
                      issue.blurb,
                      `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/newsletter`
                    )}
                    className="flex items-center gap-2 bg-[#0A1628] text-[#C5A059] text-xs font-semibold px-4 py-2 hover:bg-[#C5A059] hover:text-[#0A1628] transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <Share2 size={13} /> Share Issue
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-[#0A1628]/5 border border-[#0A1628]/10">
              <p className="text-xs text-[#2D3142]/60 leading-relaxed">
                <strong className="text-[#0A1628]">Tip:</strong> Sharing posts a link to{" "}
                <span className="font-mono text-[#0A1628]">inbody.net/newsletter</span> with your message.
                Edit the message in the share dialog before posting.
              </p>
            </div>
          </>
        )}

        {/* ── Book Interest / Launch Notify Tab ── */}
        {/* ── Analytics Tab ── */}
        {activeTab === "analytics" && <AnalyticsTab token={localStorage.getItem("admin_token")} />}

        {activeTab === "book-notify" && (
          <>
            <div className="mb-6">
              <h1 data-testid="book-notify-title" className="font-serif text-2xl font-bold text-[#0A1628]">Launch Notify List</h1>
              <span className="text-[#2D3142]/50 text-sm">{bookNotify.length} signup{bookNotify.length !== 1 ? "s" : ""} across your forthcoming books</span>
            </div>

            {bookNotify.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {Object.entries(
                  bookNotify.reduce((acc, e) => { acc[e.book] = (acc[e.book] || 0) + 1; return acc; }, {})
                ).map(([book, count]) => (
                  <div key={book} data-testid={`book-notify-count-${book.toLowerCase().replace(/ /g, "-")}`} className="border border-[#0A1628]/10 bg-white p-4">
                    <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">{book}</div>
                    <div className="font-serif text-2xl font-bold text-[#0A1628]">{count}</div>
                  </div>
                ))}
              </div>
            )}

            {bookNotifyLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : bookNotify.length === 0 ? (
              <div data-testid="empty-book-notify" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <Bell size={28} className="text-[#C5A059] mx-auto mb-3" />
                <p className="font-serif text-xl text-[#0A1628] mb-2">No launch signups yet</p>
                <p className="text-[#2D3142] text-sm">When readers tap "Notify Me at Launch" on a forthcoming book, they'll appear here.</p>
              </div>
            ) : (
              <div data-testid="book-notify-table" className="bg-white border border-[#0A1628]/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#0A1628]/10">
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">#</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Email</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Book</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Signed Up</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookNotify.map((e, idx) => (
                      <TableRow key={e.id} data-testid={`book-notify-row-${e.id}`} className="border-[#0A1628]/8 hover:bg-[#F5F0E8]/50">
                        <TableCell className="text-[#2D3142]/40 text-xs font-mono">{idx + 1}</TableCell>
                        <TableCell className="text-[#0A1628] text-sm">{e.email}</TableCell>
                        <TableCell>
                          <Badge className="bg-[#0A1628]/8 text-[#0A1628] hover:bg-[#0A1628]/8 rounded-none font-normal">{e.book}</Badge>
                        </TableCell>
                        <TableCell className="text-[#2D3142]/60 text-xs">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <button
                            data-testid={`delete-book-notify-${e.id}`}
                            onClick={() => handleDeleteNotify(e.id)}
                            title="Remove"
                            className="text-[#2D3142]/40 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ── Subscribers Tab ── */}
        {activeTab === "subscribers" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 data-testid="subscribers-title" className="font-serif text-2xl font-bold text-[#0A1628]">Mailing List</h1>
                <span className="text-[#2D3142]/50 text-sm">{subscribers.length} subscriber{subscribers.length !== 1 ? "s" : ""}</span>
              </div>
              <button
                data-testid="add-subscriber-btn"
                onClick={() => { setAddSubOpen(o => !o); setAddSubError(""); }}
                className="flex items-center gap-2 bg-[#0A1628] text-[#C5A059] text-xs font-semibold uppercase tracking-widest px-4 py-2 hover:bg-[#C5A059] hover:text-[#0A1628] transition-colors"
              >
                <Plus size={14} /> Add Contact
              </button>
            </div>

            {/* Inline add-subscriber form */}
            {addSubOpen && (
              <form
                data-testid="add-subscriber-form"
                onSubmit={handleAddSubscriber}
                className="mb-6 p-5 border border-[#0A1628]/20 bg-white"
              >
                <p className="text-xs uppercase tracking-widest font-semibold text-[#2D3142]/60 mb-4">New Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-1 block">Name</Label>
                    <Input
                      data-testid="add-sub-name"
                      placeholder="Full name (optional)"
                      value={addSubForm.name}
                      onChange={e => setAddSubForm(f => ({ ...f, name: e.target.value }))}
                      className="rounded-none border-[#0A1628]/20 bg-[#F5F0E8] focus:border-[#0A1628] focus-visible:ring-0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-1 block">Email *</Label>
                    <Input
                      data-testid="add-sub-email"
                      type="email"
                      placeholder="email@example.com"
                      value={addSubForm.email}
                      onChange={e => setAddSubForm(f => ({ ...f, email: e.target.value }))}
                      className="rounded-none border-[#0A1628]/20 bg-[#F5F0E8] focus:border-[#0A1628] focus-visible:ring-0"
                      required
                    />
                  </div>
                </div>
                {addSubError && (
                  <p data-testid="add-sub-error" className="text-red-600 text-xs mb-3">{addSubError}</p>
                )}
                <div className="flex items-center gap-3">
                  <button
                    data-testid="add-sub-submit"
                    type="submit"
                    disabled={addSubSaving}
                    className="bg-[#0A1628] text-[#C5A059] text-xs font-semibold uppercase tracking-widest px-5 py-2 hover:bg-[#C5A059] hover:text-[#0A1628] transition-colors disabled:opacity-50"
                  >
                    {addSubSaving ? "Adding..." : "Add to List"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddSubOpen(false); setAddSubError(""); setAddSubForm({ name: "", email: "" }); }}
                    className="text-xs text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {subscribersLoading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : subscribers.length === 0 ? (
              <div data-testid="empty-subscribers" className="text-center py-16 border border-[#0A1628]/10 bg-white">
                <Users size={28} className="text-[#C5A059] mx-auto mb-3" />
                <p className="font-serif text-xl text-[#0A1628] mb-2">No subscribers yet</p>
                <p className="text-[#2D3142] text-sm">Email signups from the newsletter form will appear here.</p>
              </div>
            ) : (
              <div data-testid="subscribers-table" className="bg-white border border-[#0A1628]/10 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#0A1628]/10">
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">#</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Email</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Name</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest">Subscribed</TableHead>
                      <TableHead className="text-[#2D3142] font-semibold text-xs uppercase tracking-widest"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.map((sub, idx) => (
                      <TableRow key={sub.id} data-testid={`subscriber-row-${sub.id}`} className="border-[#0A1628]/8 hover:bg-[#F5F0E8]/50">
                        <TableCell className="text-[#2D3142]/40 text-xs font-mono">{idx + 1}</TableCell>
                        {editSubId === sub.id ? (
                          <>
                            <TableCell>
                              <Input
                                data-testid={`edit-sub-email-${sub.id}`}
                                type="email"
                                value={editSubForm.email}
                                onChange={e => setEditSubForm(f => ({ ...f, email: e.target.value }))}
                                className="rounded-none border-[#0A1628]/20 bg-[#F5F0E8] focus:border-[#0A1628] focus-visible:ring-0 h-7 text-sm px-2"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                data-testid={`edit-sub-name-${sub.id}`}
                                value={editSubForm.name}
                                onChange={e => setEditSubForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Name (optional)"
                                className="rounded-none border-[#0A1628]/20 bg-[#F5F0E8] focus:border-[#0A1628] focus-visible:ring-0 h-7 text-sm px-2"
                              />
                            </TableCell>
                            <TableCell colSpan={2}>
                              <div className="flex items-center gap-2">
                                <button
                                  data-testid={`save-sub-${sub.id}`}
                                  onClick={() => handleEditSubSave(sub.id)}
                                  disabled={editSubSaving}
                                  className="bg-[#0A1628] text-[#C5A059] text-xs font-semibold px-3 py-1 hover:bg-[#C5A059] hover:text-[#0A1628] transition-colors disabled:opacity-50"
                                >
                                  {editSubSaving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  onClick={() => { setEditSubId(null); setEditSubError(""); }}
                                  className="text-xs text-[#2D3142]/50 hover:text-[#0A1628] transition-colors"
                                >Cancel</button>
                                {editSubError && <span className="text-red-600 text-xs">{editSubError}</span>}
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              <a href={`mailto:${sub.email}`} className="text-sm text-[#0A1628] hover:text-[#C5A059] transition-colors">
                                {sub.email}
                              </a>
                            </TableCell>
                            <TableCell className="text-[#2D3142] text-sm">{sub.name || <span className="text-[#2D3142]/30">—</span>}</TableCell>
                            <TableCell className="text-[#2D3142] text-xs">{formatDate(sub.subscribed_at)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-3">
                                {confirmDeleteSubId === sub.id ? (
                                  <span className="flex items-center gap-2 text-xs">
                                    <span className="text-red-600 font-medium">Remove?</span>
                                    <button
                                      data-testid={`confirm-delete-sub-${sub.id}`}
                                      onClick={() => handleDeleteSubscriber(sub.id)}
                                      className="bg-red-600 text-white px-2 py-0.5 text-xs font-semibold hover:bg-red-700 transition-colors"
                                    >Yes</button>
                                    <button
                                      onClick={() => setConfirmDeleteSubId(null)}
                                      className="text-[#2D3142]/40 hover:text-[#0A1628] transition-colors text-xs"
                                    >No</button>
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      data-testid={`edit-sub-${sub.id}`}
                                      onClick={() => { setEditSubId(sub.id); setEditSubForm({ name: sub.name || "", email: sub.email }); setEditSubError(""); setConfirmDeleteSubId(null); }}
                                      className="text-[#2D3142]/30 hover:text-[#0A1628] transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      data-testid={`delete-sub-${sub.id}`}
                                      onClick={() => { setConfirmDeleteSubId(sub.id); setEditSubId(null); }}
                                      className="text-[#2D3142]/30 hover:text-red-600 transition-colors"
                                      title="Remove from list"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-[#F5F0E8] border border-[#0A1628]/20 rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A1628]">
              {editingPost ? "Edit Post" : "New Blog Post"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                data-testid="post-title-input"
                placeholder="Enter post title..."
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Categories * <span className="normal-case font-normal">(select all that apply)</span></Label>
              <div data-testid="post-category-select" className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => {
                  const selected = form.categories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setForm(f => ({
                        ...f,
                        categories: selected
                          ? f.categories.filter(c => c !== cat)
                          : [...f.categories, cat]
                      }))}
                      className={`px-3 py-1 text-xs font-medium border transition-colors duration-150 ${
                        selected
                          ? "bg-[#0A1628] text-[#F5F0E8] border-[#0A1628]"
                          : "bg-white text-[#2D3142] border-[#0A1628]/20 hover:border-[#0A1628]"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Excerpt *</Label>
              <Textarea
                value={form.excerpt}
                onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                data-testid="post-excerpt-input"
                placeholder="Brief summary shown in the blog listing..."
                rows={2}
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0 resize-none"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Content *</Label>
              {/* Key forces remount when switching between new/edit so content resets */}
              <TiptapEditor
                key={editingPost?.id || "new"}
                initialContent={form.content}
                onChange={val => setForm(f => ({ ...f, content: val }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                data-testid="post-published-toggle"
                className={`w-10 h-6 transition-colors relative ${form.published ? "bg-green-500" : "bg-[#0A1628]/20"}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white transition-all ${form.published ? "left-5" : "left-1"}`} />
              </button>
              <Label className="text-sm text-[#0A1628] cursor-pointer" onClick={() => setForm(f => ({ ...f, published: !f.published }))}>
                {form.published
                  ? <span className="flex items-center gap-1"><Eye size={12} /> Published</span>
                  : <span className="flex items-center gap-1"><EyeOff size={12} /> Draft</span>}
              </Label>
            </div>
            {formError && <p className="text-red-500 text-sm bg-red-50 border border-red-200 px-3 py-2">{formError}</p>}
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-post-btn"
              className="rounded-none border-[#0A1628]/20"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-post-btn"
              className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
            >
              {saving ? "Saving..." : <><Check size={14} className="mr-2" />{editingPost ? "Update Post" : "Create Post"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Post Confirm Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#0A1628]">Delete Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The post will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="bg-red-600 text-white hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Upload Modal */}
      <Dialog open={!!uploadModalPaperId} onOpenChange={() => setUploadModalPaperId(null)}>
        <DialogContent className="max-w-md bg-[#F5F0E8] border border-[#0A1628]/20 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg text-[#0A1628]">Upload PDF</DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-[#0A1628]/15 -mx-6 px-6 mb-5">
            {[
              { id: "computer", label: "From Computer" },
              { id: "url", label: "Paste Link" },
              { id: "drive", label: "Google Drive" },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => { setUploadTab(t.id); setUrlError(""); }}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                  uploadTab === t.id ? "border-[#0A1628] text-[#0A1628]" : "border-transparent text-[#2D3142]/50 hover:text-[#0A1628]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Computer Upload */}
          {uploadTab === "computer" && (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#0A1628]/20 bg-white p-8 cursor-pointer hover:border-[#0A1628]/40 transition-colors">
              <Upload size={28} className="text-[#C5A059] mb-3" />
              <p className="text-sm font-medium text-[#0A1628] mb-1">Click to select a PDF</p>
              <p className="text-xs text-[#2D3142]/50">or drag and drop</p>
              <input
                type="file"
                accept="application/pdf"
                className="sr-only"
                disabled={!!uploadingId}
                onChange={e => uploadModalPaperId && handlePdfUpload(uploadModalPaperId, e.target.files[0])}
              />
              {uploadingId && <p className="text-xs text-[#C5A059] mt-3 animate-pulse">Uploading…</p>}
            </label>
          )}

          {/* URL Paste */}
          {uploadTab === "url" && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Share Link</Label>
                <Input
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  placeholder="https://drive.google.com/file/d/... or OneDrive link"
                  className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0 text-sm"
                  data-testid="url-upload-input"
                />
              </div>
              <div className="text-xs text-[#2D3142]/50 bg-white border border-[#0A1628]/10 p-3 space-y-1">
                <p className="font-semibold text-[#0A1628]">Supported links:</p>
                <p>Google Drive: Share → "Anyone with the link" → Copy link</p>
                <p>OneDrive: Share → "Anyone with the link" → Copy link</p>
              </div>
              {urlError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 px-3 py-2">{urlError}</p>}
              <Button
                onClick={() => uploadModalPaperId && handleUrlUpload(uploadModalPaperId)}
                disabled={urlUploading || !urlInput.trim()}
                data-testid="url-upload-btn"
                className="w-full bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
              >
                {urlUploading ? "Fetching…" : "Fetch & Upload"}
              </Button>
            </div>
          )}

          {/* Google Drive Picker */}
          {uploadTab === "drive" && (
            <div className="space-y-4">
              <Button
                onClick={() => uploadModalPaperId && openGooglePicker(uploadModalPaperId)}
                disabled={!!uploadingId}
                data-testid="google-drive-picker-btn"
                className="w-full bg-white border border-[#0A1628]/20 text-[#0A1628] hover:bg-[#F5F0E8] rounded-none flex items-center gap-3 justify-center py-3"
              >
                <svg viewBox="0 0 87.3 78" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                  <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                  <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                  <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                  <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                  <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                  <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 27h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                </svg>
                {uploadingId ? "Uploading from Drive…" : "Browse Google Drive"}
              </Button>

              {urlError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 px-3 py-2">{urlError}</p>}

              {!process.env.REACT_APP_GOOGLE_API_KEY && (
                <div className="text-xs bg-amber-50 border border-amber-200 p-3 space-y-1 text-amber-800">
                  <p className="font-semibold">Setup required — one-time Google Cloud config:</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Go to <span className="font-mono">console.cloud.google.com</span></li>
                    <li>Enable "Google Picker API" &amp; "Google Drive API"</li>
                    <li>Create an API Key (restrict to Google Picker API)</li>
                    <li>Create an OAuth 2.0 Client ID (Web App type)</li>
                    <li>Add <span className="font-mono">https://inbody.net</span> as authorized JS origin</li>
                    <li>Share your API Key + Client ID here and I'll add them</li>
                  </ol>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Paper Create/Edit Dialog */}
      <Dialog open={paperDialogOpen} onOpenChange={setPaperDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#F5F0E8] border border-[#0A1628]/20 rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A1628]">
              {editingPaper ? "Edit Paper" : "New Paper"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Title *</Label>
              <Input
                value={paperForm.title}
                onChange={e => setPaperForm(f => ({ ...f, title: e.target.value }))}
                data-testid="paper-title-input"
                placeholder="Paper title..."
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Categories * <span className="normal-case font-normal">(select all that apply)</span></Label>
                <div data-testid="paper-category-select" className="flex flex-wrap gap-2">
                  {PAPER_CATEGORIES.map(cat => {
                    const selected = paperForm.categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setPaperForm(f => ({
                          ...f,
                          categories: selected
                            ? f.categories.filter(c => c !== cat)
                            : [...f.categories, cat]
                        }))}
                        className={`px-3 py-1 text-xs font-medium border transition-colors duration-150 ${
                          selected
                            ? "bg-[#0A1628] text-[#F5F0E8] border-[#0A1628]"
                            : "bg-white text-[#2D3142] border-[#0A1628]/20 hover:border-[#0A1628]"
                        }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Year *</Label>
                <Input
                  type="number"
                  value={paperForm.year}
                  onChange={e => setPaperForm(f => ({ ...f, year: parseInt(e.target.value) || f.year }))}
                  data-testid="paper-year-input"
                  min="1900"
                  max="2100"
                  className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Abstract *</Label>
              <Textarea
                value={paperForm.abstract}
                onChange={e => setPaperForm(f => ({ ...f, abstract: e.target.value }))}
                data-testid="paper-abstract-input"
                placeholder="A concise summary of the paper's content and findings..."
                rows={5}
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0 resize-none"
              />
            </div>
            {paperFormError && <p className="text-red-500 text-sm bg-red-50 border border-red-200 px-3 py-2">{paperFormError}</p>}
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setPaperDialogOpen(false)}
              data-testid="cancel-paper-btn"
              className="rounded-none border-[#0A1628]/20"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={handleSavePaper}
              disabled={paperSaving}
              data-testid="save-paper-btn"
              className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
            >
              {paperSaving ? "Saving..." : <><Check size={14} className="mr-2" />{editingPaper ? "Update Paper" : "Create Paper"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Paper Confirm Dialog */}
      <AlertDialog open={!!deletePaperId} onOpenChange={() => setDeletePaperId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#0A1628]">Delete Paper?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the paper and its uploaded PDF if any.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-paper-btn" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePaper}
              data-testid="confirm-delete-paper-btn"
              className="bg-red-600 text-white hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Essay Create/Edit Dialog */}
      <Dialog open={essayDialogOpen} onOpenChange={setEssayDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#F5F0E8] border border-[#0A1628]/20 rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A1628]">
              {editingEssay ? "Edit Essay" : "New Essay"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Title *</Label>
              <Input
                value={essayForm.title}
                onChange={e => setEssayForm(f => ({ ...f, title: e.target.value }))}
                data-testid="essay-title-input"
                placeholder="Essay title..."
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Categories * <span className="normal-case font-normal">(select all that apply)</span></Label>
                <div data-testid="essay-category-select" className="flex flex-wrap gap-2">
                  {PAPER_CATEGORIES.map(cat => {
                    const selected = essayForm.categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setEssayForm(f => ({
                          ...f,
                          categories: selected
                            ? f.categories.filter(c => c !== cat)
                            : [...f.categories, cat]
                        }))}
                        className={`px-3 py-1 text-xs font-medium border transition-colors duration-150 ${
                          selected
                            ? "bg-[#0A1628] text-[#F5F0E8] border-[#0A1628]"
                            : "bg-white text-[#2D3142] border-[#0A1628]/20 hover:border-[#0A1628]"
                        }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Year *</Label>
                <Input
                  type="number"
                  value={essayForm.year}
                  onChange={e => setEssayForm(f => ({ ...f, year: parseInt(e.target.value) || f.year }))}
                  data-testid="essay-year-input"
                  min="1900"
                  max="2100"
                  className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Description *</Label>
              <Textarea
                value={essayForm.abstract}
                onChange={e => setEssayForm(f => ({ ...f, abstract: e.target.value }))}
                data-testid="essay-abstract-input"
                placeholder="A brief description of the essay..."
                rows={3}
                className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0 resize-none"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">
                Essay Content <span className="normal-case font-normal text-[#2D3142]/40">(write or paste the full essay here)</span>
              </Label>
              {essayDialogOpen && (
                <TiptapEditor
                  key={editingEssay?.id ?? "new-essay"}
                  initialContent={essayForm.content}
                  onChange={html => setEssayForm(f => ({ ...f, content: html }))}
                />
              )}
            </div>
            {essayFormError && <p className="text-red-500 text-sm bg-red-50 border border-red-200 px-3 py-2">{essayFormError}</p>}
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setEssayDialogOpen(false)}
              data-testid="cancel-essay-btn"
              className="rounded-none border-[#0A1628]/20"
            >
              <X size={14} className="mr-2" /> Cancel
            </Button>
            <Button
              onClick={handleSaveEssay}
              disabled={essaySaving}
              data-testid="save-essay-btn"
              className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
            >
              {essaySaving ? "Saving..." : <><Check size={14} className="mr-2" />{editingEssay ? "Update Essay" : "Create Essay"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Essay Confirm Dialog */}
      <AlertDialog open={!!deleteEssayId} onOpenChange={() => setDeleteEssayId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#0A1628]">Delete Essay?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the essay and any uploaded file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-essay-btn" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEssay}
              data-testid="confirm-delete-essay-btn"
              className="bg-red-600 text-white hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Essay Upload Modal */}
      <Dialog open={!!uploadModalEssayId} onOpenChange={v => { if (!v) { setUploadModalEssayId(null); setUrlInput(""); setUrlError(""); }}}>
        <DialogContent className="max-w-md bg-[#F5F0E8] border border-[#0A1628]/20 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg text-[#0A1628]">Upload Essay File</DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-[#0A1628]/15 -mx-6 px-6 mb-5">
            {[
              { id: "computer", label: "From Computer" },
              { id: "url",      label: "Paste Link"    },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => { setUploadTab(t.id); setUrlError(""); }}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                  uploadTab === t.id ? "border-[#0A1628] text-[#0A1628]" : "border-transparent text-[#2D3142]/50 hover:text-[#0A1628]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Computer Upload */}
          {uploadTab === "computer" && (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#0A1628]/20 bg-white p-8 cursor-pointer hover:border-[#0A1628]/40 transition-colors">
              <Upload size={28} className="text-[#C5A059] mb-3" />
              <p className="text-sm font-medium text-[#0A1628] mb-1">Click to select a PDF</p>
              <p className="text-xs text-[#2D3142]/50">or drag and drop</p>
              <input
                type="file"
                accept="application/pdf"
                className="sr-only"
                disabled={!!uploadingEssayId}
                onChange={e => uploadModalEssayId && handleEssayFileUpload(uploadModalEssayId, e.target.files[0])}
              />
              {uploadingEssayId && <p className="text-xs text-[#C5A059] mt-3 animate-pulse">Uploading…</p>}
            </label>
          )}

          {/* URL Paste */}
          {uploadTab === "url" && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">File Link</Label>
                <Input
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  placeholder="https://drive.google.com/file/d/... or direct PDF/DOCX URL"
                  className="rounded-none border-[#0A1628]/20 bg-white focus:border-[#0A1628] focus-visible:ring-0 text-sm"
                  data-testid="essay-url-upload-input"
                />
              </div>
              <div className="text-xs text-[#2D3142]/50 bg-white border border-[#0A1628]/10 p-3 space-y-1">
                <p className="font-semibold text-[#0A1628]">Supported links:</p>
                <p>Google Drive: Share → "Anyone with the link" → Copy link</p>
                <p>OneDrive: Share → "Anyone with the link" → Copy link</p>
                <p>Direct PDF or DOCX URL</p>
              </div>
              {urlError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 px-3 py-2">{urlError}</p>}
              <Button
                onClick={() => uploadModalEssayId && handleEssayUrlUpload(uploadModalEssayId)}
                disabled={urlUploading || !urlInput.trim()}
                data-testid="essay-url-upload-btn"
                className="w-full bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
              >
                {urlUploading ? "Fetching…" : "Fetch & Upload"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Social Share Modal ── */}
      <Dialog open={shareOpen} onOpenChange={v => { setShareOpen(v); if (!v) setShareResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0A1628] flex items-center gap-2">
              <Share2 size={16} className="text-[#C5A059]" /> Share to Social Media
            </DialogTitle>
          </DialogHeader>

          {shareTitle && (
            <div className="space-y-5 py-1">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#2D3142]/50 mb-1">Item</p>
                <p className="text-sm font-medium text-[#0A1628]">{shareTitle}</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Message</Label>
                <Textarea
                  data-testid="share-message-input"
                  value={shareMsg}
                  onChange={e => setShareMsg(e.target.value)}
                  rows={4}
                  className="rounded-none border-[#0A1628]/20 bg-[#F5F0E8] focus:border-[#0A1628] focus-visible:ring-0 resize-none text-sm"
                  placeholder="What would you like to say about this post?"
                />
                <p className="text-xs text-[#2D3142]/40 mt-1">The post URL will be appended automatically.</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2 block">Post To</Label>
                <div className="flex gap-3">
                  {[
                    { key: "bluesky", label: "BlueSky" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      data-testid={`share-platform-${key}`}
                      type="button"
                      onClick={() => setSharePlatforms(p => ({ ...p, [key]: !p[key] }))}
                      className={`px-4 py-2 text-xs font-semibold border transition-colors ${
                        sharePlatforms[key]
                          ? "bg-[#0A1628] text-[#C5A059] border-[#0A1628]"
                          : "bg-white text-[#2D3142]/50 border-[#0A1628]/20 hover:border-[#0A1628]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              {shareResults && (
                <div data-testid="share-results" className="space-y-2">
                  {shareResults._error && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2">{shareResults._error}</p>
                  )}
                  {shareResults.bluesky && (
                    <div className={`flex items-start gap-2 px-3 py-2 border text-xs ${
                      shareResults.bluesky.success ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"
                    }`}>
                      <span className="font-semibold">BlueSky:</span>
                      {shareResults.bluesky.success
                        ? <span>Posted successfully</span>
                        : <span>{shareResults.bluesky.error}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShareOpen(false)} className="rounded-none border-[#0A1628]/20">
              <X size={14} className="mr-2" /> Close
            </Button>
            {!shareResults && (
              <Button
                data-testid="share-submit-btn"
                onClick={handleShare}
                disabled={shareSaving || !Object.values(sharePlatforms).some(Boolean) || !shareMsg.trim()}
                className="bg-[#0A1628] text-[#F5F0E8] hover:bg-[#2D3142] rounded-none"
              >
                {shareSaving ? "Posting…" : <><Share2 size={14} className="mr-2" />Post Now</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
